/**
 * What locking Armadillo by hand does while the report's automatic
 * assignment still bills its orders: the manual assignment takes it over.
 * Said in both lock confirms (the card's and the chat aside's) before the
 * analyst commits to it.
 */

import { Alert } from '@mantine/core';

import { isRegistryPartner, type NetworkPartnerSummary } from '@/types/network-partner';
import {
  assignmentLabelFor,
  liveManagedAssignment,
  type BackofficeAssignment,
} from '@/types/np-assignments';
import { formatGbp } from '@/types/registry-documents';

/** The confirm button's words when the lock takes the automatic one over. */
export const TAKEOVER_CONFIRM_LABEL = 'Lock in and take over';

/** The automatic assignment locking `partner` on `target` would take over:
 * the live one, when the partner is Armadillo; null otherwise. */
export function registryTakeoverOf(
  assignments: readonly BackofficeAssignment[],
  target: Pick<BackofficeAssignment, 'id'>,
  partner: Pick<NetworkPartnerSummary, 'registry_partner'> | null | undefined
): BackofficeAssignment | null {
  if (!isRegistryPartner(partner)) {
    return null;
  }
  const managed = liveManagedAssignment(assignments);
  return managed && managed.id !== target.id ? managed : null;
}

/** The confirm button's words: activating a prospect first, taking the
 * automatic assignment over, or a plain lock. */
export function lockConfirmLabel(isProspect: boolean, takeover: boolean): string {
  if (isProspect) {
    return 'Activate & lock';
  }
  return takeover ? TAKEOVER_CONFIRM_LABEL : 'Lock in';
}

/** "its 3 registry orders move to this assignment and are billed here". */
function ordersMove(count: number): string {
  return count === 1
    ? 'its 1 registry order moves to this assignment and is billed here'
    : `its ${count} registry orders move to this assignment and are billed here`;
}

/** The notice itself. `target` is the assignment being locked: the Hub
 * settles its fee on the orders, so a fee typed there is replaced (both
 * figures stay in the fee history). */
export function RegistryTakeoverNotice({
  assignments,
  managed,
  target,
}: Readonly<{
  assignments: readonly BackofficeAssignment[];
  managed: BackofficeAssignment;
  target: Pick<BackofficeAssignment, 'npFee'>;
}>) {
  const label = assignmentLabelFor(assignments, managed.id);
  const settled =
    target.npFee == null
      ? 'Its fee is set to that figure.'
      : `The ${formatGbp(target.npFee)} typed here is replaced by that figure, both kept in the fee history.`;
  return (
    <Alert color="blue" data-testid="registry-takeover-notice">
      This takes over the automatic assignment{label ? ` ${label}` : ''}:{' '}
      {ordersMove(managed.registryOrders.length)}, and the automatic one closes (its Backoffice
      request, if any, goes to £0). The Hub then keeps this assignment&apos;s fee in step with the
      orders: their total plus any adjustment, changed with &quot;Adjust fee&quot;. {settled}
    </Alert>
  );
}
